import { KycAttachmentMeta } from "@/types/kycReview";
import { Draft } from "../state/types";
import { hasValue as hasIdValue } from "./idValidation";
import { compactObject, getPath, isYes } from "./paths";
import { normalizeMobileNumber } from "./phone";
import { riskQuestions, selectedRiskOption } from "./riskProfiler";

export interface BuildPayloadArgs {
  draft: Draft;
  isTier3: boolean;
  riskDirty: boolean;
  attachments: KycAttachmentMeta[];
}

const FATCA_CLEARED = {
  usPerson: false,
  usCitizen: false,
  usResident: false,
  usAlien: false,
  usPhone: false,
  usBorn: false,
  usAddressResidence: false,
  usAddressMailing: false,
  usAccount: false,
  usPOA: false,
  usCareOfAddress: false,
};

function pepRequiresDocument(draft: Draft): boolean {
  return (
    isYes(getPath(draft.kyc, "PEP.governmentWork")) ||
    isYes(getPath(draft.kyc, "PEP.governmentRelative"))
  );
}

function idValue(value: unknown): unknown | undefined {
  return hasIdValue(value) ? value : undefined;
}

function buildIdsPayload(ids: Draft["kyc"]["ids"]): Record<string, unknown> {
  const tin = idValue(ids?.TIN);
  const sss = idValue(ids?.SSS);
  const hasExpiration = ids?.hasExpiration !== false;

  return {
    TIN: tin,
    SSS: sss,
    hasExpiration,
    idValidity: hasExpiration ? ids?.idValidity : "",
  };
}

function buildRiskQuestionnaireSubmitPayload(
  draft: Draft
): Record<string, unknown> {
  return riskQuestions.reduce<Record<string, unknown>>((payload, question) => {
    const selected = selectedRiskOption(draft.riskQuestionnaire, question);
    if (!selected) return payload;

    payload[question.category] = {
      [`${question.prefix}1`]: selected === 1,
      [`${question.prefix}2`]: selected === 2,
      [`${question.prefix}3`]: selected === 3,
    };

    return payload;
  }, {});
}

// Tier-aware KYC payload. Editable-except-email: personal identity fields are
// sent for both tiers. Tier 2 keeps its Short KYC address/ID/work scope, while
// Tier 3 adds the remaining Long KYC groups. `email` is never included.
export function buildKycSubmitPayload(
  args: BuildPayloadArgs
): Record<string, unknown> {
  const { draft, isTier3 } = args;
  const k = draft.kyc;

  if (!isTier3) {
    return (
      (compactObject({
        name: {
          firstName: k.name?.firstName,
          lastName: k.name?.lastName,
          middleName: k.name?.middleName,
          suffix: k.name?.suffix,
        },
        dateOfBirth: k.dateOfBirth,
        placeOfBirth: k.placeOfBirth,
        cityOfBirth: k.cityOfBirth,
        nationality: k.nationality,
        gender: k.gender,
        civil: k.civil,
        currentAddress: k.currentAddress,
        ids: buildIdsPayload(k.ids),
        work: {
          primaryOccupation: k.work?.primaryOccupation,
          sourceOfFunds: k.work?.sourceOfFunds,
        },
      }) as Record<string, unknown>) || {}
    );
  }

  const allowedKyc = {
    name: {
      firstName: k.name?.firstName,
      lastName: k.name?.lastName,
      middleName: k.name?.middleName,
      suffix: k.name?.suffix,
    },
    dateOfBirth: k.dateOfBirth,
    placeOfBirth: k.placeOfBirth,
    cityOfBirth: k.cityOfBirth,
    nationality: k.nationality,
    gender: k.gender,
    civil: k.civil,
    contact: k.contact,
    ids: buildIdsPayload(k.ids),
    currentAddress: k.currentAddress,
    permanentAddress: k.permanentAddress,
    businessAddress: k.businessAddress,
    work: {
      primaryOccupation: k.work?.primaryOccupation,
      nameOfEmployer: k.work?.nameOfEmployer,
      businessNature: k.work?.businessNature,
      annualIncomeAmount: k.work?.annualIncomeAmount,
    },
    PEP: pepRequiresDocument(draft) ? k.PEP : { ...k.PEP, banks: "" },
    FATCA: isYes(getPath(draft.kyc, "FATCA.usPerson"))
      ? k.FATCA
      : FATCA_CLEARED,
  };

  return (compactObject(allowedKyc) as Record<string, unknown>) || {};
}

// Full submit body for POST /v2/kyc-review/update. First/last name also go
// through `user` for profile consistency; risk is only sent when edited;
// settlement is Tier 3 only.
export function buildSubmitPayload(
  args: BuildPayloadArgs
): Record<string, unknown> {
  const { draft, isTier3, riskDirty, attachments } = args;
  const mobileNumber =
    draft.user.mobileNumber === undefined
      ? undefined
      : normalizeMobileNumber(draft.user.mobileNumber, { countryCode: "ph" });

  const user =
    (compactObject({
      firstName: draft.user.firstName,
      lastName: draft.user.lastName,
      mobileNumber,
    }) as Record<string, unknown>) || {};

  const payload: Record<string, unknown> = {
    user,
    kyc: buildKycSubmitPayload(args),
    attachments,
  };

  if (riskDirty) {
    payload.riskQuestionnaire = buildRiskQuestionnaireSubmitPayload(draft);
  }

  if (isTier3) {
    payload.settlementDetails =
      (compactObject(draft.accountDetails) as Record<string, unknown>) || {};
  }

  return payload;
}
